import type { AppliedEvidenceRule } from './dailyOverrideTypes'
import { consumerEvidenceLabel } from './evidenceConsumerLabels'
import type { WellnessPillars } from './submitMorningWellness'
import { WELLNESS_INDIVIDUAL_CRITICAL } from '../../supabase/functions/_shared/wellnessEngine.ts'

function aggregateWellnessScore(pillars: WellnessPillars): number {
  return pillars.stress + pillars.fatigue + pillars.sleep_quality + pillars.muscle_soreness + pillars.mood
}

function wellnessPullTriggers(pillars: WellnessPillars): { pull: boolean; reasons: string[] } {
  const reasons: string[] = []
  for (const [key, value] of Object.entries(pillars) as [keyof WellnessPillars, number][]) {
    if (value >= WELLNESS_INDIVIDUAL_CRITICAL) {
      reasons.push(`${key.replace(/_/g, ' ')} rated ${value}/5 — critical recovery threshold reached.`)
    }
  }
  return { pull: reasons.length > 0, reasons }
}

/** Client-side preview of protocol adjustments driven by today's subjective check-in. */
export function buildWellnessProtocolPreview(pillars: WellnessPillars): AppliedEvidenceRule[] {
  const rules: AppliedEvidenceRule[] = []
  const aggregate = aggregateWellnessScore(pillars)
  const { pull, reasons } = wellnessPullTriggers(pillars)

  if (pillars.stress >= WELLNESS_INDIVIDUAL_CRITICAL || pillars.mood >= WELLNESS_INDIVIDUAL_CRITICAL) {
    rules.push(previewRule('K6', 'Stress–Recovery Balance', reasons))
  }
  if (pillars.fatigue >= WELLNESS_INDIVIDUAL_CRITICAL || pillars.sleep_quality >= WELLNESS_INDIVIDUAL_CRITICAL) {
    rules.push(previewRule('K4', 'Training Volume Autoregulation', reasons))
  }
  if (pillars.muscle_soreness >= WELLNESS_INDIVIDUAL_CRITICAL) {
    rules.push(previewRule('K5', 'Recovery Debt Accumulation', reasons))
  }
  if (pull) {
    rules.push(previewRule('M4', 'Protein Target: Muscle Recovery Support', reasons))
    rules.push(previewRule('M8', 'Meal Timing Window', reasons))
  } else if (aggregate <= 12) {
    rules.push(previewRule('M2', 'Energy Load: Training Volume Alignment', reasons))
  }

  return dedupeRules(rules)
}

function previewRule(
  ruleKey: string,
  label: string,
  reasons: string[],
): AppliedEvidenceRule {
  return {
    rule_key: ruleKey,
    mechanism_family: label,
    physiological_mechanism:
      'Your morning check-in shapes how Coach Kilo calibrates training load and nutrition for today.',
    action_summary: consumerEvidenceLabel(ruleKey, label),
    evidence_refs: [],
    personal_signals: reasons.slice(0, 3),
  }
}

function dedupeRules(rules: AppliedEvidenceRule[]): AppliedEvidenceRule[] {
  const seen = new Set<string>()
  return rules.filter((rule) => {
    if (seen.has(rule.rule_key)) return false
    seen.add(rule.rule_key)
    return true
  })
}
